import type { OverlapPush } from "../../api/fastApi/types";
import type { ShiftPatch } from "./overlapCascade";
import type { AudioTrackData } from "./types";

/**
 * Толчки, которые сервер ручной пары сделал сам (правило владельца 01.10.2026: «ты должен всегда
 * сдвигать соседние реплики иначе будут наслоения») и назвал в ответе — поле `pushed`
 * переозвучки, проверенной озвучки, разбиения и склейки перевода (контракт C4).
 *
 * Массив — сервер прошёл папку (пустой — никого не сдвинул): редактору пересчитывать нечего,
 * только положить эти места у себя и назвать их. Свой проход по копии папки ставил бы соседку
 * иначе, чем сервер, и автосохранение возвращало бы наложение на диск.
 * null — прохода на сервере не было (старый сервер, пара не ручная) или он не удался
 * (`overlap_push_failed`): тогда наложения снимает раздвижка редактора, как раньше.
 */
export function serverPushesOf(response: unknown): OverlapPush[] | null {
    if (!response || typeof response !== "object") return null;
    const answer = response as { pushed?: unknown; overlap_push_failed?: unknown };
    if (typeof answer.overlap_push_failed === "string" && answer.overlap_push_failed) {
        console.warn("[OVERLAP_PUSH] проход наложений на сервере не удался:", answer.overlap_push_failed);
        return null;
    }
    if (!Array.isArray(answer.pushed)) return null;
    return answer.pushed
        .filter((one): one is OverlapPush => Boolean(one) && typeof one === "object" && (one as OverlapPush).id != null)
        .map((one) => ({ ...one, id: String(one.id) }));
}

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * Места, куда сервер поставил толкнутые реплики, — патчами для данных редактора: `start` и те
 * ключи места, что он назвал. `start_rus_milli` не трогается: сервер его не двигает (это слот,
 * выбранный человеком), и перечитанная папка должна совпасть с экраном. Толчок без числового
 * `start` пропускается — ставить некуда, папку покажет перечитывание.
 */
export function serverPushPatches(pushes: OverlapPush[]): ShiftPatch[] {
    return pushes
        .filter((push) => isNumber(push.start))
        .map((push) => {
            const patch: Partial<AudioTrackData> = { start: push.start as number };
            if (isNumber(push.shifted_start)) patch.shifted_start = push.shifted_start;
            if (isNumber(push.new_start)) patch.new_start = push.new_start;
            return { trackId: push.id, patch };
        });
}

/** На сколько мс сервер сдвинул реплику: его `delta_ms`, иначе разница мест; не знаем — undefined. */
export function serverPushDeltaMs(push: OverlapPush, localStart?: number): number | undefined {
    if (isNumber(push.delta_ms)) return Math.round(push.delta_ms);
    const from = isNumber(push.from) ? push.from : localStart;
    if (isNumber(push.start) && isNumber(from)) return Math.round(push.start - from);
    return undefined;
}

/**
 * Текст нейтральной полосы «Наложение снято…» — одинаковый для сдвигов редактора и сервера:
 * переводчик должен видеть, какие озвучки передвинул не он и на сколько. `at` — время реплики
 * на дорожке, `by` — на сколько мс (неизвестно — не называется, а не выдумывается).
 */
export function overlapPushNotice(moves: { at: string; by?: number }[]): string | null {
    if (!moves.length) return null;
    if (moves.length === 1) {
        const [{ at, by }] = moves;
        return `Наложение снято: озвучка ${at} отодвинута позже${
            by !== undefined ? ` на ${by} мс` : ""
        } — на конец предыдущей озвучки + 50 мс.`;
    }
    return `Наложения сняты: озвучки ${moves
        .map(({ at, by }) => (by !== undefined ? `${at} (+${by} мс)` : at))
        .join(", ")} отодвинуты позже — каждая на конец предыдущей озвучки + 50 мс.`;
}
